import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Save, CalendarPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  DAY_NAMES,
  PRESETS,
  SESSION_TYPES,
  TIMEZONES,
  createAdhocSession,
  deleteCourseSchedule,
  deleteSession,
  fmtDateTime,
  getCourseSchedule,
  listCourseSessions,
  presetSlots,
  saveCourseSchedule,
  statusBadge,
  typeBadge,
} from "@/lib/schedule";

const todayISO = () => new Date().toISOString().slice(0, 10);
const inWeeksISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n * 7);
  return d.toISOString().slice(0, 10);
};

function SlotRow({ slot, idx, onChange, onRemove }) {
  return (
    <div
      className="grid grid-cols-12 gap-2 items-center"
      data-testid={`slot-row-${idx}`}
    >
      <Select
        value={String(slot.day_of_week)}
        onValueChange={(v) => onChange({ ...slot, day_of_week: Number(v) })}
      >
        <SelectTrigger className="col-span-4" data-testid={`slot-day-${idx}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DAY_NAMES.map((n, i) => (
            <SelectItem key={i} value={String(i)}>
              {n}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="time"
        value={slot.start_time}
        onChange={(e) => onChange({ ...slot, start_time: e.target.value })}
        className="col-span-4"
        data-testid={`slot-time-${idx}`}
      />
      <Input
        type="number"
        min={15}
        max={480}
        step={15}
        value={slot.duration_minutes}
        onChange={(e) =>
          onChange({ ...slot, duration_minutes: Number(e.target.value) || 60 })
        }
        className="col-span-3"
        data-testid={`slot-duration-${idx}`}
      />
      <Button
        size="icon"
        variant="ghost"
        onClick={onRemove}
        className="col-span-1"
        data-testid={`slot-remove-${idx}`}
      >
        <Trash2 className="h-4 w-4 text-stone-500" />
      </Button>
    </div>
  );
}

export default function SchedulePanel({ course }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [sessions, setSessions] = useState([]);

  // form state
  const [preset, setPreset] = useState("twice_weekday");
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(inWeeksISO(3));
  const [tz, setTz] = useState("Asia/Kolkata");
  const [slots, setSlots] = useState(presetSlots("twice_weekday"));

  // conflict dialog
  const [conflicts, setConflicts] = useState(null);

  // ad-hoc dialog
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [adhocForm, setAdhocForm] = useState({
    starts_at: "",
    duration_minutes: 60,
    title: "",
    session_type: "classwork",
    linked_chapter_id: "",
    external_link: "",
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const [sc, ss] = await Promise.all([
        getCourseSchedule(course.id),
        listCourseSessions(course.id),
      ]);
      setSchedule(sc);
      setSessions(ss);
      if (sc) {
        setPreset(sc.preset);
        setStartDate(sc.start_date);
        setEndDate(sc.end_date);
        setTz(sc.timezone);
        setSlots(sc.slots);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    refresh();
  }, [course.id]);

  const onPresetChange = (v) => {
    setPreset(v);
    if (v !== "custom") setSlots(presetSlots(v));
  };

  const addSlot = () =>
    setSlots((arr) => [
      ...arr,
      { day_of_week: 0, start_time: "10:00", duration_minutes: 60 },
    ]);

  const updateSlot = (i, val) =>
    setSlots((arr) => arr.map((s, idx) => (idx === i ? val : s)));

  const removeSlot = (i) =>
    setSlots((arr) => arr.filter((_, idx) => idx !== i));

  const onSave = async () => {
    if (slots.length === 0) {
      toast.error("Add at least one slot");
      return;
    }
    setBusy(true);
    try {
      const res = await saveCourseSchedule(course.id, {
        start_date: startDate,
        end_date: endDate,
        timezone: tz,
        slots,
        preset,
      });
      toast.success(`Schedule saved · ${res.sessions_created} sessions generated`);
      await refresh();
    } catch (err) {
      if (err?.response?.status === 409) {
        setConflicts(err.response.data?.detail?.conflicts || []);
      } else {
        toast.error(err?.response?.data?.detail || "Save failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const onClear = async () => {
    setBusy(true);
    try {
      await deleteCourseSchedule(course.id);
      toast.success("Schedule cleared");
      setSchedule(null);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const onCreateAdhoc = async () => {
    if (!adhocForm.starts_at) {
      toast.error("Pick a start time");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        starts_at: new Date(adhocForm.starts_at).toISOString(),
        duration_minutes: Number(adhocForm.duration_minutes) || 60,
        title: adhocForm.title || undefined,
        session_type: adhocForm.session_type,
        linked_chapter_id: adhocForm.linked_chapter_id || undefined,
        external_link: adhocForm.external_link || undefined,
      };
      await createAdhocSession(course.id, payload);
      toast.success("Ad-hoc session created");
      setAdhocOpen(false);
      setAdhocForm({
        starts_at: "",
        duration_minutes: 60,
        title: "",
        session_type: "classwork",
        linked_chapter_id: "",
        external_link: "",
      });
      await refresh();
    } catch (err) {
      if (err?.response?.status === 409) {
        setConflicts(err.response.data?.detail?.conflicts || []);
        setAdhocOpen(false);
      } else {
        toast.error(err?.response?.data?.detail || "Create failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const onDeleteSession = async (id) => {
    try {
      await deleteSession(id);
      toast.success("Session removed");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const futureSessions = useMemo(
    () =>
      sessions
        .filter((s) => new Date(s.starts_at) >= new Date(Date.now() - 60 * 60 * 1000))
        .slice(0, 30),
    [sessions]
  );

  if (loading) {
    return (
      <div className="py-12 text-center text-stone-500" data-testid="schedule-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin mb-2" /> Loading schedule…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="schedule-panel">
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="text-base">Recurring schedule</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* preset radios */}
          <div className="space-y-2">
            <Label>Preset</Label>
            <div className="space-y-2" role="radiogroup" data-testid="preset-group">
              {PRESETS.map((p) => (
                <label
                  key={p.value}
                  className={`flex items-start gap-3 border rounded-md px-3 py-2 cursor-pointer ${
                    preset === p.value
                      ? "border-stone-900 bg-stone-50"
                      : "border-stone-200 hover:border-stone-400"
                  }`}
                  data-testid={`preset-${p.value}`}
                >
                  <input
                    type="radio"
                    name="preset"
                    value={p.value}
                    checked={preset === p.value}
                    onChange={() => onPresetChange(p.value)}
                    className="mt-1 accent-stone-900"
                  />
                  <span className="text-sm">{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Start date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="schedule-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>End date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="schedule-end-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger data-testid="schedule-tz">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Slots</Label>
              <Button
                size="sm"
                variant="outline"
                onClick={addSlot}
                data-testid="add-slot-button"
              >
                <Plus className="mr-1 h-3.5 w-3.5" /> Add slot
              </Button>
            </div>
            {slots.length === 0 && (
              <p className="text-xs text-stone-500">No slots — add at least one.</p>
            )}
            <div className="space-y-2">
              {slots.map((s, i) => (
                <SlotRow
                  key={i}
                  slot={s}
                  idx={i}
                  onChange={(v) => updateSlot(i, v)}
                  onRemove={() => removeSlot(i)}
                />
              ))}
            </div>
            <p className="text-[11px] text-stone-500">
              Slot times are in the schedule timezone. Sessions are generated for every
              matching weekday in the date range.
            </p>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              onClick={onSave}
              disabled={busy}
              className="bg-stone-900 hover:bg-stone-800 text-white"
              data-testid="schedule-save-button"
            >
              <Save className="mr-1 h-4 w-4" /> Save &amp; generate sessions
            </Button>
            {schedule && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-700"
                    data-testid="schedule-clear-button"
                  >
                    <Trash2 className="mr-1 h-4 w-4" /> Clear schedule
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear recurring schedule?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Future scheduled sessions linked to this schedule will be
                      removed. Completed and ad-hoc sessions are kept.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={onClear}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="schedule-clear-confirm"
                    >
                      Clear
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sessions list */}
      <Card className="border-stone-200" data-testid="sessions-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">
            Upcoming sessions ({futureSessions.length})
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAdhocOpen(true)}
            data-testid="add-adhoc-button"
          >
            <CalendarPlus className="mr-1 h-4 w-4" /> Add ad-hoc session
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {futureSessions.length === 0 ? (
            <div className="py-8 text-center text-sm text-stone-500" data-testid="no-sessions">
              No upcoming sessions yet.
            </div>
          ) : (
            futureSessions.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-3 px-4 py-3 border-b border-stone-100 last:border-b-0"
                data-testid={`session-row-${s.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.title}</div>
                  <div className="text-xs text-stone-500 truncate">
                    {fmtDateTime(s.starts_at)} · {Math.round(
                      (new Date(s.ends_at) - new Date(s.starts_at)) / 60000
                    )}{" "}
                    min
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={typeBadge(s.session_type)}>
                    {s.session_type}
                  </Badge>
                  <Badge variant="outline" className={statusBadge(s.status)}>
                    {s.status}
                  </Badge>
                  {s.status === "scheduled" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`delete-session-${s.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-stone-500" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove this session?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDeleteSession(s.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Ad-hoc dialog */}
      <Dialog open={adhocOpen} onOpenChange={setAdhocOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add an ad-hoc session</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Title (optional)</Label>
              <Input
                value={adhocForm.title}
                onChange={(e) =>
                  setAdhocForm((f) => ({ ...f, title: e.target.value }))
                }
                placeholder="e.g. Doubt-clearing session"
                data-testid="adhoc-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Starts at (local)</Label>
                <Input
                  type="datetime-local"
                  value={adhocForm.starts_at}
                  onChange={(e) =>
                    setAdhocForm((f) => ({ ...f, starts_at: e.target.value }))
                  }
                  data-testid="adhoc-starts-at"
                />
              </div>
              <div className="space-y-1">
                <Label>Duration (min)</Label>
                <Input
                  type="number"
                  min={15}
                  max={480}
                  step={15}
                  value={adhocForm.duration_minutes}
                  onChange={(e) =>
                    setAdhocForm((f) => ({
                      ...f,
                      duration_minutes: Number(e.target.value) || 60,
                    }))
                  }
                  data-testid="adhoc-duration"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Type</Label>
                <Select
                  value={adhocForm.session_type}
                  onValueChange={(v) =>
                    setAdhocForm((f) => ({ ...f, session_type: v }))
                  }
                >
                  <SelectTrigger data-testid="adhoc-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SESSION_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Link to chapter</Label>
                <Select
                  value={adhocForm.linked_chapter_id || "none"}
                  onValueChange={(v) =>
                    setAdhocForm((f) => ({
                      ...f,
                      linked_chapter_id: v === "none" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger data-testid="adhoc-chapter">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {course.chapters
                      .slice()
                      .sort((a, b) => a.order - b.order)
                      .map((ch, i) => (
                        <SelectItem key={ch.id} value={ch.id}>
                          {i + 1}. {ch.title}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>External link (optional)</Label>
              <Input
                value={adhocForm.external_link}
                onChange={(e) =>
                  setAdhocForm((f) => ({ ...f, external_link: e.target.value }))
                }
                placeholder="https://… (overrides built-in Jitsi room if set)"
                data-testid="adhoc-external-link"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdhocOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={onCreateAdhoc}
              disabled={busy}
              className="bg-stone-900 hover:bg-stone-800 text-white"
              data-testid="adhoc-create-button"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict dialog */}
      <Dialog open={!!conflicts} onOpenChange={() => setConflicts(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule conflict detected</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">
            One or more sessions overlap with an existing booking for this teacher.
            Adjust the slot times or remove the conflicting sessions and try again.
          </p>
          <div className="space-y-1 max-h-60 overflow-auto">
            {(conflicts || []).map((c) => (
              <div
                key={c.session_id}
                className="text-xs border border-stone-200 rounded px-2 py-1"
                data-testid={`conflict-${c.session_id}`}
              >
                <div className="font-medium">{c.title}</div>
                <div className="text-stone-500">
                  {fmtDateTime(c.starts_at)} – {fmtDateTime(c.ends_at)}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setConflicts(null)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
